import * as React from 'react'
import styled from 'styled-components'

import * as glyphs from '../../icons/glyphs'
import { EditButton } from '../button/EditButton'
import { LinkAsButton } from '../button/LinkAsButton'
import { Tag } from '../tag/Tag'
import { VideoLoader, type VideoLoaderProps } from '../VideoLoader'
import { Link } from '../Link'

import * as styles from '../../styles'

export type Media =
  | { type: 'image'; src: string }
  | {
      consentWall: VideoLoaderProps['consentWall']
      type: 'video'
      placeholder: string
      videoHTML: string
    }

export type NewsCardProps = {
  isEditMode?: boolean
  className?: string
  content?: string
  editingLink?: string
  href: string
  isLocked: boolean
  isVideoActive?: boolean
  media?: Media
  date: string | undefined
  onVideoMount?: () => void
  title: string
  translations: {
    editCTA?: string
    commentCount: string
    locked: string
    readCTA?: string
    subscribeCTA?: string
  }
}

const NewsCardComponent = React.forwardRef<HTMLDivElement, NewsCardProps>(function NewsCardComponent(
  {
    isEditMode = false,
    content,
    editingLink,
    href,
    isLocked,
    isVideoActive = true,
    date,
    onVideoMount,
    title,
    translations,
    media,
  },
  ref,
): React.ReactElement {
  const isMounted = useIsMounted()

  return (
    <styles.card.news.NewsCardWrapper ref={ref}>
      {isEditMode && editingLink && (
        <a href={editingLink}>
          <EditButton glyph={<glyphs.stroke.Edit size={18} />}>{translations.editCTA}</EditButton>
        </a>
      )}
      <Link href={href}>
        {media && (
          <styles.card.news.NewsCardMediaWrapper isLocked={isLocked} type={media.type}>
            {media.type === 'image' && <styles.card.news.NewsCardImage src={media.src} alt={title} loading="lazy" />}
            {media.type === 'video' && (
              <VideoLoader
                consentWall={media.consentWall}
                isActive={isVideoActive}
                onVideoMount={onVideoMount}
                isLocked={isLocked}
                placeholder={media.placeholder}
                videoHtml={media.videoHTML}
              />
            )}

            <styles.card.news.LockLabel asDesktop="L" isLocked={isLocked} type={media.type}>
              <glyphs.stroke.Lock size={21} />
              {translations.locked}
            </styles.card.news.LockLabel>
          </styles.card.news.NewsCardMediaWrapper>
        )}
        <styles.card.news.NewsCardContainer>
          <styles.card.news.NewsCardHeader>
            {date && <styles.card.news.NewsCardDate>{date}</styles.card.news.NewsCardDate>}
            {isLocked && (
              <Tag color="sand">
                <glyphs.stroke.Lock size={10} />
                {translations.locked}
              </Tag>
            )}
          </styles.card.news.NewsCardHeader>
          <styles.card.news.NewsCardTitle asDesktop="S">{title}</styles.card.news.NewsCardTitle>
          {content && (
            <styles.card.news.NewsCardDescription dangerouslySetInnerHTML={{ __html: content }} isLocked={isLocked} />
          )}
          <styles.card.news.NewsCardFooter>
            <styles.card.news.CommentsContainer>
              <glyphs.stroke.Bubble size={11} />
              <styles.card.news.CommentsCount>{translations.commentCount}</styles.card.news.CommentsCount>
            </styles.card.news.CommentsContainer>
            <styles.card.news.SubscriptionContainer>
              <styles.card.news.SubscriptionText>
                {/* NOTE: There is a bug that forces us to render some buttons client side, for no apparent reason. */}
                {isMounted && (
                  <SubscriptionCTA tinted>
                    {' '}
                    {isLocked ? translations.subscribeCTA : translations.readCTA}
                  </SubscriptionCTA>
                )}
              </styles.card.news.SubscriptionText>
              <glyphs.stroke.ArrowRight size={16} />
            </styles.card.news.SubscriptionContainer>
          </styles.card.news.NewsCardFooter>
        </styles.card.news.NewsCardContainer>
      </Link>
    </styles.card.news.NewsCardWrapper>
  )
})

function useIsMounted(): boolean {
  const [isMounted, setIsMounted] = React.useState(false)

  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  return isMounted
}

export const NewsCard = styled(NewsCardComponent)``

const SubscriptionCTA = styled(LinkAsButton)`
  padding: 13px 0;
`
